import { InboxIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@ui/lib/utils'

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Was fehlt — in einem kurzen Satz. */
  title: string
  /** Optional: was der Nutzer tun kann, um den Zustand zu ändern. */
  description?: string
  /** Optional: ein Icon aus lucide-react. Standard ist ein Posteingang. */
  icon?: React.ReactNode
  /** Optional: die naheliegende Aktion, z.B. ein Button "Neu anlegen". */
  action?: React.ReactNode
}

/**
 * Leerer Zustand — wird angezeigt, wenn eine Liste keine Einträge hat.
 * Sagt, was fehlt und was der Nutzer tun kann; keine Entschuldigungen.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="Keine Vorgänge vorhanden"
 *   description="Sobald ein Vorgang angelegt wurde, erscheint er hier."
 *   action={<Button onClick={onCreate}>Vorgang anlegen</Button>}
 * />
 * ```
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center',
        className,
      )}
      {...props}
    >
      <div className="text-muted-foreground [&_svg]:size-8">
        {icon ?? <InboxIcon aria-hidden="true" />}
      </div>
      <div className="space-y-1">
        <p className="text-base font-medium text-foreground">{title}</p>
        {description && (
          <p className="max-w-prose text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </div>
  )
}
